import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Star, Check, X, Edit2, Trash2, ShieldCheck, MessageSquare, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 10;

const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [filteredReviews, setFilteredReviews] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [editingReview, setEditingReview] = useState(null);
  const [editText, setEditText] = useState('');
  const [products, setProducts] = useState({}); // Map of product_id -> product
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchReviews();
  }, []);

  useEffect(() => {
    filterReviews();
    setPage(1); // reset to first page when filter changes
  }, [statusFilter, reviews]);

  const totalPages = Math.max(1, Math.ceil(filteredReviews.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginatedReviews = filteredReviews.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const fetchReviews = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/api/reviews/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviews(response.data);
      
      // Fetch product details for all reviews
      const productIds = [...new Set(response.data.map(r => r.product_id))];
      const productPromises = productIds.map(id =>
        axios.get(`${API_URL}/api/products/${id}`).catch(() => null)
      );
      const productResponses = await Promise.all(productPromises);
      
      // Create product lookup map
      const productMap = {};
      productResponses.forEach((res, idx) => {
        if (res && res.data) {
          productMap[productIds[idx]] = res.data;
        }
      });
      setProducts(productMap);
    } catch (error) {
      toast.error('Failed to fetch reviews');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterReviews = () => {
    if (statusFilter === 'all') {
      setFilteredReviews(reviews);
    } else {
      setFilteredReviews(reviews.filter(r => r.status === statusFilter));
    }
  };

  const approveReview = async (reviewId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API_URL}/api/reviews/admin/${reviewId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Review approved');
      fetchReviews();
    } catch (error) {
      toast.error('Failed to approve review');
    }
  };

  const rejectReview = async (reviewId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API_URL}/api/reviews/admin/${reviewId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Review rejected');
      fetchReviews();
    } catch (error) {
      toast.error('Failed to reject review');
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API_URL}/api/reviews/admin/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Review deleted');
      fetchReviews();
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const startEdit = (review) => {
    setEditingReview(review.id);
    setEditText(review.review_text || '');
  };

  const saveEdit = async (reviewId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API_URL}/api/reviews/admin/${reviewId}`, 
        { review_text: editText },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Review updated');
      setEditingReview(null);
      fetchReviews();
    } catch (error) {
      toast.error('Failed to update review');
    }
  };

  const getStatusBadge = (status) => {
    // Default to 'approved' if status is undefined (old reviews)
    const reviewStatus = status || 'approved';
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
      approved: 'bg-green-500/20 text-green-400 border-green-500/50',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/50'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${styles[reviewStatus]}`}>
        {reviewStatus.charAt(0).toUpperCase() + reviewStatus.slice(1)}
      </span>
    );
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-600'}`}
      />
    ));
  };

  const stats = {
    total: reviews.length,
    pending: reviews.filter(r => r.status === 'pending').length,
    approved: reviews.filter(r => r.status === 'approved' || !r.status).length, // Old reviews without status count as approved
    rejected: reviews.filter(r => r.status === 'rejected').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-white">Loading reviews...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-2">
          <MessageSquare className="w-8 h-8" />
          Review Management
        </h1>
        <p className="text-gray-400">Approve, reject, edit, or delete customer reviews</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-6">
            <div className="text-3xl font-bold text-white">{stats.total}</div>
            <div className="text-sm text-gray-400">Total Reviews</div>
          </CardContent>
        </Card>
        <Card className="bg-gray-900 border-yellow-500/30">
          <CardContent className="p-6">
            <div className="text-3xl font-bold text-yellow-400">{stats.pending}</div>
            <div className="text-sm text-gray-400">Pending Approval</div>
          </CardContent>
        </Card>
        <Card className="bg-gray-900 border-green-500/30">
          <CardContent className="p-6">
            <div className="text-3xl font-bold text-green-400">{stats.approved}</div>
            <div className="text-sm text-gray-400">Approved</div>
          </CardContent>
        </Card>
        <Card className="bg-gray-900 border-red-500/30">
          <CardContent className="p-6">
            <div className="text-3xl font-bold text-red-400">{stats.rejected}</div>
            <div className="text-sm text-gray-400">Rejected</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        <Button
          onClick={() => setStatusFilter('all')}
          variant={statusFilter === 'all' ? 'default' : 'outline'}
          className={statusFilter === 'all' ? 'bg-blue-500' : ''}
        >
          All ({stats.total})
        </Button>
        <Button
          onClick={() => setStatusFilter('pending')}
          variant={statusFilter === 'pending' ? 'default' : 'outline'}
          className={statusFilter === 'pending' ? 'bg-yellow-500' : ''}
        >
          Pending ({stats.pending})
        </Button>
        <Button
          onClick={() => setStatusFilter('approved')}
          variant={statusFilter === 'approved' ? 'default' : 'outline'}
          className={statusFilter === 'approved' ? 'bg-green-500' : ''}
        >
          Approved ({stats.approved})
        </Button>
        <Button
          onClick={() => setStatusFilter('rejected')}
          variant={statusFilter === 'rejected' ? 'default' : 'outline'}
          className={statusFilter === 'rejected' ? 'bg-red-500' : ''}
        >
          Rejected ({stats.rejected})
        </Button>
      </div>

      {/* Reviews List */}
      <div className="space-y-4">
        {filteredReviews.length === 0 ? (
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-12 text-center">
              <MessageSquare className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No reviews found</p>
            </CardContent>
          </Card>
        ) : (
          paginatedReviews.map((review) => (
            <Card key={review.id} className="bg-gray-900 border-gray-800">
              <CardContent className="p-6">
                {/* Product Info */}
                {products[review.product_id] && (
                  <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-800">
                    <img
                      src={products[review.product_id].images?.[0] || 'https://via.placeholder.com/60'}
                      alt={products[review.product_id].title}
                      className="w-12 h-12 rounded-lg object-contain bg-white"
                    />
                    <div>
                      <p className="text-xs text-gray-500">Review for:</p>
                      <p className="text-sm font-medium text-white">{products[review.product_id].title}</p>
                    </div>
                  </div>
                )}
                
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-white">{review.user_name}</h3>
                      {getStatusBadge(review.status)}
                      {review.is_verified_purchase && (
                        <span className="flex items-center gap-1 text-xs text-green-400">
                          <ShieldCheck className="w-4 h-4" />
                          Verified Purchase
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      {renderStars(review.rating)}
                      <span className="text-sm text-gray-400">
                        {new Date(review.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mb-3">{review.user_email}</p>
                    
                    {editingReview === review.id ? (
                      <div className="mb-3">
                        <textarea
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          className="w-full px-4 py-2 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-blue-500 outline-none"
                          rows="3"
                        />
                        <div className="flex gap-2 mt-2">
                          <Button onClick={() => saveEdit(review.id)} size="sm" className="bg-green-500">
                            Save
                          </Button>
                          <Button onClick={() => setEditingReview(null)} size="sm" variant="outline">
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-300">{review.review_text}</p>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 pt-4 border-t border-gray-800">
                  {review.status === 'pending' && (
                    <>
                      <Button
                        onClick={() => approveReview(review.id)}
                        size="sm"
                        className="bg-green-500 hover:bg-green-600"
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        onClick={() => rejectReview(review.id)}
                        size="sm"
                        variant="outline"
                        className="border-red-500 text-red-500 hover:bg-red-500/10"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                    </>
                  )}
                  {review.status === 'approved' && (
                    <Button
                      onClick={() => rejectReview(review.id)}
                      size="sm"
                      variant="outline"
                      className="border-red-500 text-red-500"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                  )}
                  {review.status === 'rejected' && (
                    <Button
                      onClick={() => approveReview(review.id)}
                      size="sm"
                      className="bg-green-500"
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Approve
                    </Button>
                  )}
                  <Button
                    onClick={() => startEdit(review)}
                    size="sm"
                    variant="outline"
                    className="border-blue-500 text-blue-500"
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    onClick={() => deleteReview(review.id)}
                    size="sm"
                    variant="outline"
                    className="border-red-500 text-red-500"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Pagination Controls */}
      {filteredReviews.length > 0 && (
        <div className="flex items-center justify-between bg-gray-900 border border-gray-800 rounded-lg px-6 py-4">
          <p className="text-sm text-gray-400" data-testid="admin-reviews-pagination-info">
            Showing{' '}
            <span className="font-medium text-white">
              {(currentPage - 1) * PAGE_SIZE + 1}
            </span>
            {' '}to{' '}
            <span className="font-medium text-white">
              {Math.min(currentPage * PAGE_SIZE, filteredReviews.length)}
            </span>
            {' '}of{' '}
            <span className="font-medium text-white">{filteredReviews.length}</span>
            {' '}reviews
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              data-testid="admin-reviews-prev-page"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <span className="text-sm text-gray-400 px-2">
              Page <span className="font-medium text-white">{currentPage}</span> of{' '}
              <span className="font-medium text-white">{totalPages}</span>
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              data-testid="admin-reviews-next-page"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReviews;
